import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { getAuth, multiFactor, onAuthStateChanged, sendEmailVerification, EmailAuthProvider, linkWithCredential } from 'firebase/auth';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import CircularProgress from '@material-ui/core/CircularProgress';
import Alert from '@material-ui/lab/Alert';
import MFAEnrollment from './MFAEnrollment';
import API, { GetUserSyncEmailVerificationPathForApi, GetUserForceVerifyEmailPathForApi } from '../../Util/api';

// Firebase TOTP MFA requires a supported first factor (email/password or native OAuth).
// Custom token auth (e.g. Intuit) is not supported.
const supportedMFAProviders = ['password', 'google.com', 'apple.com'];
const isMFASupported = (user) => {
    if (!user) return false;
    return user.providerData.some(p => supportedMFAProviders.includes(p.providerId));
};

const isEmailVerified = (user) => {
    if (!user) return false;
    const hasPasswordProvider = user.providerData.some(p => p.providerId === 'password');
    if (!hasPasswordProvider) return true;
    return user.emailVerified;
};

const MFAEnrollmentGate = ({ children, mfaRequired }) => {
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(true);
    const [mfaEnrolled, setMfaEnrolled] = useState(false);
    const [mfaSupported, setMfaSupported] = useState(true);
    const [emailVerified, setEmailVerified] = useState(null);

    // Password establishment for unsupported providers
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [settingPassword, setSettingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    // Email verification
    const [sendingVerification, setSendingVerification] = useState(false);
    const [verificationError, setVerificationError] = useState('');
    const [verificationMessage, setVerificationMessage] = useState('');

    const checkMFAStatus = useCallback(async () => {
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                setMfaSupported(isMFASupported(user));
                setEmailVerified(isEmailVerified(user));
                const factors = multiFactor(user).enrolledFactors;
                setMfaEnrolled(factors.length > 0);

                // Sync email verification from OAuth providers
                try {
                    await API.post(GetUserSyncEmailVerificationPathForApi());
                    await user.reload();
                    const freshUser = auth.currentUser;
                    if (freshUser) {
                        setEmailVerified(isEmailVerified(freshUser));
                        setMfaEnrolled(multiFactor(freshUser).enrolledFactors.length > 0);
                    }
                } catch (err) {
                    console.debug('Email verification sync failed (non-critical):', err);
                }
            }
        } catch (err) {
            console.error('Error checking MFA status:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        checkMFAStatus();
    }, [checkMFAStatus]);

    useEffect(() => {
        try {
            const auth = getAuth();
            const unsubscribe = onAuthStateChanged(auth, (user) => {
                if (user) {
                    checkMFAStatus();
                }
            });
            return () => unsubscribe();
        } catch (err) {
            return () => {};
        }
    }, [checkMFAStatus]);

    const handleEnrollmentComplete = () => {
        sessionStorage.setItem('mfaGateChecked', 'true');
        setMfaEnrolled(true);
    };

    const handleSendVerificationEmail = async () => {
        setSendingVerification(true);
        setVerificationError('');
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            await sendEmailVerification(user);
            setVerificationMessage('Verification email sent! Please check your inbox.');
        } catch (err) {
            setVerificationError(err.message || 'Failed to send verification email.');
        } finally {
            setSendingVerification(false);
        }
    };

    const handleCheckVerification = async () => {
        setIsLoading(true);
        setVerificationError('');
        setVerificationMessage('');
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                try {
                    await API.post(GetUserSyncEmailVerificationPathForApi());
                    await user.reload();
                    const freshUser = auth.currentUser;
                    if (freshUser) {
                        const verified = isEmailVerified(freshUser);
                        setEmailVerified(verified);
                        if (!verified) {
                            setVerificationError('Email not verified yet. Please check your inbox and click the verification link.');
                        } else {
                            setVerificationMessage('Email verified successfully!');
                        }
                    }
                } catch (err) {
                    setVerificationError('Failed to sync verification status.');
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSetPassword = async () => {
        setPasswordError('');
        if (newPassword.length < 6) {
            setPasswordError('Password must be at least 6 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError('Passwords do not match.');
            return;
        }
        setSettingPassword(true);
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            // user.email may be null for Intuit/custom-token users whose Firebase Auth record
            // was created without an email. Fall back to the email embedded in the token claims.
            let email = user.email;
            if (!email) {
                const tokenResult = await user.getIdTokenResult();
                email = tokenResult.claims.email;
            }
            if (!email) {
                setPasswordError('Unable to determine your email address. Please sign out and sign back in.');
                return;
            }
            // Use linkWithCredential to add email+password as a provider.
            // updatePassword only works when a password provider already exists;
            // Intuit/custom-token users have no providers and need linkWithCredential.
            const credential = EmailAuthProvider.credential(email, newPassword);
            await linkWithCredential(user, credential);
            // linkWithCredential resets emailVerified to false even if the Admin SDK
            // had set it. Force-verify via backend (identity already proven via Intuit OAuth).
            await API.post(GetUserForceVerifyEmailPathForApi());
            await user.reload();
            setMfaSupported(true);
            setEmailVerified(true);
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            console.error('Error setting password:', err);
            if (err.code === 'auth/requires-recent-login') {
                setPasswordError('Your session has expired. Please sign out and sign back in, then try again.');
            } else if (err.code === 'auth/weak-password') {
                setPasswordError('Password is too weak. Please choose a stronger password.');
            } else if (err.code === 'auth/email-already-in-use') {
                setPasswordError('A password is already set for this account. Please sign out and sign in with your password.');
            } else {
                setPasswordError(err.message || 'Failed to set password.');
            }
        } finally {
            setSettingPassword(false);
        }
    };

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </div>
        );
    }

    // If MFA is not required or already enrolled, render children
    if (!mfaRequired || mfaEnrolled) {
        return children;
    }

    if (mfaRequired && !mfaEnrolled) {
        // User's sign-in method doesn't support MFA (e.g. Intuit custom token)
        // Prompt them to set a password to add a supported first factor
        if (!mfaSupported) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
                    <Typography variant="h6" style={{ marginBottom: 16 }}>
                        Your company requires multi-factor authentication
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24, maxWidth: 500, textAlign: 'center' }}>
                        You signed in via QuickBooks (Intuit), which doesn't support MFA directly. To enable MFA, please set a password for your account — you can then continue signing in via QuickBooks as usual.
                    </Typography>

                    {passwordError && (
                        <Alert severity="error" style={{ marginBottom: 16, maxWidth: 400 }} onClose={() => setPasswordError('')}>
                            {passwordError}
                        </Alert>
                    )}

                    <div style={{ width: '100%', maxWidth: 400 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            type="password"
                            label="New password"
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            style={{ marginBottom: 16 }}
                        />
                        <TextField
                            fullWidth
                            variant="outlined"
                            type="password"
                            label="Confirm password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSetPassword(); }}
                            style={{ marginBottom: 16 }}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={handleSetPassword}
                            disabled={settingPassword}
                        >
                            {settingPassword ? <CircularProgress size={20} /> : 'Set Password & Continue'}
                        </Button>
                    </div>
                    <Link component="button" variant="body2" onClick={() => history.push('/signout')} style={{ marginTop: 24 }}>
                        Sign out
                    </Link>
                </div>
            );
        }

        // Email not verified — show verification flow first
        if (emailVerified === false) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
                    <Typography variant="h6" style={{ marginBottom: 16 }}>
                        Your company requires multi-factor authentication
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
                        You must verify your email address before setting up MFA.
                    </Typography>

                    {verificationError && (
                        <Alert severity="error" style={{ marginBottom: 16, maxWidth: 500 }} onClose={() => setVerificationError('')}>
                            {verificationError}
                        </Alert>
                    )}
                    {verificationMessage && (
                        <Alert severity="success" style={{ marginBottom: 16, maxWidth: 500 }} onClose={() => setVerificationMessage('')}>
                            {verificationMessage}
                        </Alert>
                    )}

                    <div style={{ display: 'flex', gap: 16 }}>
                        <Button
                            variant="outlined"
                            onClick={handleSendVerificationEmail}
                            disabled={sendingVerification}
                        >
                            {sendingVerification ? <CircularProgress size={16} /> : 'Send Verification Email'}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCheckVerification}
                        >
                            I've Verified My Email
                        </Button>
                    </div>
                    <Link component="button" variant="body2" onClick={() => history.push('/signout')} style={{ marginTop: 24 }}>
                        Sign out
                    </Link>
                </div>
            );
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
                <Typography variant="h6" style={{ marginBottom: 16 }}>
                    Your company requires multi-factor authentication
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
                    Please set up an authenticator app to continue using AP Central.
                </Typography>
                <MFAEnrollment onEnrollmentComplete={handleEnrollmentComplete} />
                <Link component="button" variant="body2" onClick={() => history.push('/signout')} style={{ marginTop: 24 }}>
                    Sign out
                </Link>
            </div>
        );
    }

    return children;
};

export default MFAEnrollmentGate;
